import { endOfDay, format, startOfDay } from "date-fns";
import { dateOnlyToDatePickerValue, datePickerValueToDateOnly, isDateOnlyString } from "@ibada/shared-types";

export type TableFilterValues = Record<string, string>;

export type TableFilterVisibilityRule = {
  key: string;
  equals: string;
};

export type TableFilterOperatorOption = {
  value: string;
  label: string;
};

type TableFilterBaseConfig = {
  id: string;
  label: string;
  testId?: string;
  placeholder?: string;
  description?: string;
  hidden?: boolean;
  visibleWhen?: TableFilterVisibilityRule;
  disabled?: boolean;
  operators?: TableFilterOperatorOption[];
  defaultOperator?: string;
};

export type TableFilterTextFieldConfig = TableFilterBaseConfig & {
  type: "text";
  key: string;
  defaultValue?: string;
};

export type TableFilterSelectOption = {
  value: string;
  label: string;
};

export type TableFilterSelectFieldConfig = TableFilterBaseConfig & {
  type: "select";
  key: string;
  options: TableFilterSelectOption[];
  defaultValue?: string;
  creatable?: boolean;
  formatCreateLabel?: (inputValue: string) => string;
};

export type TableFilterLookupFieldConfig = TableFilterBaseConfig & {
  type: "lookup";
  key: string;
  options: TableFilterSelectOption[];
  defaultValue?: string;
  searchValue?: string;
  onSearchValueChange?: (value: string) => void;
  searchPlaceholder?: string;
  isLoading?: boolean;
  loadingMessage?: string;
  mobileEmpty?: string;
};

export type TableFilterAsyncSelectFieldConfig = TableFilterBaseConfig & {
  type: "asyncSelect";
  key: string;
  options: TableFilterSelectOption[];
  defaultValue?: string;
  searchValue?: string;
  onSearchValueChange?: (value: string) => void;
  searchPlaceholder?: string;
  isLoading?: boolean;
  loadingMessage?: string;
  mobileEmpty?: string;
};

export type TableFilterMultiSelectFieldConfig = TableFilterBaseConfig & {
  type: "multiSelect";
  key: string;
  options: TableFilterSelectOption[];
  defaultValues?: string[];
  emptyLabel?: string;
  creatable?: boolean;
  formatCreateLabel?: (inputValue: string) => string;
};

export type TableFilterBooleanFieldConfig = TableFilterBaseConfig & {
  type: "boolean";
  key: string;
  defaultValue?: "" | "true" | "false";
  trueLabel?: string;
  falseLabel?: string;
};

export type TableFilterDateTimeFieldConfig = TableFilterBaseConfig & {
  type: "dateTime";
  key: string;
  defaultValue?: string;
};

export type TableFilterDateRangePresetId =
  | "today"
  | "last7Days"
  | "last30Days"
  | "last3Months"
  | "last6Months"
  | "last12Months"
  | "all";

export type TableFilterDateRangePreset = {
  id: TableFilterDateRangePresetId;
  label: string;
};

export type TableFilterDateRangeFieldConfig = {
  id: string;
  type: "dateRange";
  label: string;
  /** Optional QA hook matching other filter field configs. */
  testId?: string;
  description?: string;
  hidden?: boolean;
  visibleWhen?: TableFilterVisibilityRule;
  disabled?: boolean;
  operators?: TableFilterOperatorOption[];
  defaultOperator?: string;
  fromKey: string;
  toKey: string;
  fromLabel?: string;
  toLabel?: string;
  fromPlaceholder?: string;
  toPlaceholder?: string;
  fromDefaultValue?: string;
  toDefaultValue?: string;
  presets?: TableFilterDateRangePreset[];
};

export type TableFilterNumberRangeFieldConfig = {
  id: string;
  type: "numberRange";
  label: string;
  testId?: string;
  description?: string;
  hidden?: boolean;
  visibleWhen?: TableFilterVisibilityRule;
  disabled?: boolean;
  operators?: TableFilterOperatorOption[];
  defaultOperator?: string;
  minKey: string;
  maxKey: string;
  minLabel?: string;
  maxLabel?: string;
  minPlaceholder?: string;
  maxPlaceholder?: string;
  minDefaultValue?: string;
  maxDefaultValue?: string;
};

export type TableFilterFieldConfig =
  | TableFilterTextFieldConfig
  | TableFilterSelectFieldConfig
  | TableFilterLookupFieldConfig
  | TableFilterAsyncSelectFieldConfig
  | TableFilterMultiSelectFieldConfig
  | TableFilterBooleanFieldConfig
  | TableFilterDateTimeFieldConfig
  | TableFilterDateRangeFieldConfig
  | TableFilterNumberRangeFieldConfig;

export type TableFilterSearchConfig = {
  value: string;
  onChange: (value: string) => void;
  testId?: string;
  placeholder?: string;
  debounceMs?: number;
  className?: string;
};

export type TableFilterConfig = {
  title?: string;
  description?: string;
  buttonLabel?: string;
  cancelLabel?: string;
  resetLabel?: string;
  applyLabel?: string;
  clearLabel?: string;
  addFilterButtonTestId?: string;
  columnsLabel?: string;
  refreshButtonTestId?: string;
  refreshLabel?: string;
  fields?: TableFilterFieldConfig[];
  search?: TableFilterSearchConfig;
};

export type DataTableFilterChipState = {
  field: TableFilterFieldConfig;
  operator: string;
  isApplied: boolean;
  valueLabel: string;
};

const DEFAULT_OPERATORS: Record<TableFilterFieldConfig["type"], TableFilterOperatorOption[]> = {
  text: [{ value: "contains", label: "contains" }],
  select: [{ value: "is", label: "is" }],
  lookup: [{ value: "is", label: "is" }],
  asyncSelect: [{ value: "is", label: "is" }],
  multiSelect: [{ value: "in", label: "in" }],
  boolean: [{ value: "is", label: "is" }],
  dateTime: [{ value: "on", label: "on" }],
  dateRange: [{ value: "between", label: "between" }],
  numberRange: [{ value: "between", label: "between" }],
};

const DEFAULT_RANGE_PRESETS: TableFilterDateRangePreset[] = [
  { id: "today", label: "Today" },
  { id: "last7Days", label: "Last 7 Days" },
  { id: "last30Days", label: "Last 30 Days" },
  { id: "last3Months", label: "Last 3 Months" },
  { id: "last6Months", label: "Last 6 Months" },
  { id: "last12Months", label: "Last 12 Months" },
  { id: "all", label: "All" },
];

export function getDateRangePresets(field: TableFilterDateRangeFieldConfig): TableFilterDateRangePreset[] {
  return field.presets?.length ? field.presets : DEFAULT_RANGE_PRESETS;
}

export function getFieldOperators(field: TableFilterFieldConfig): TableFilterOperatorOption[] {
  if (field.type === "dateRange") {
    return field.operators?.length ? field.operators : DEFAULT_OPERATORS.dateRange;
  }

  if (field.type === "numberRange") {
    return field.operators?.length ? field.operators : DEFAULT_OPERATORS.numberRange;
  }

  return field.operators?.length ? field.operators : DEFAULT_OPERATORS[field.type];
}

export function getDefaultOperator(field: TableFilterFieldConfig): string {
  if (field.defaultOperator?.trim()) {
    return field.defaultOperator;
  }

  return getFieldOperators(field)[0]?.value ?? "is";
}

export function getFieldValueKeys(field: TableFilterFieldConfig): string[] {
  if (field.type === "dateRange") {
    return [field.fromKey, field.toKey];
  }

  if (field.type === "numberRange") {
    return [field.minKey, field.maxKey];
  }

  return [field.key];
}

export function getFieldDefaultValues(field: TableFilterFieldConfig): TableFilterValues {
  if (field.type === "dateRange") {
    return {
      [field.fromKey]: field.fromDefaultValue ?? "",
      [field.toKey]: field.toDefaultValue ?? "",
    };
  }

  if (field.type === "numberRange") {
    return {
      [field.minKey]: field.minDefaultValue ?? "",
      [field.maxKey]: field.maxDefaultValue ?? "",
    };
  }

  if (field.type === "multiSelect") {
    return {
      [field.key]: serializeMultiSelectValue(field.defaultValues ?? []),
    };
  }

  if (field.type === "boolean") {
    return {
      [field.key]: field.defaultValue ?? "",
    };
  }

  return {
    [field.key]: field.defaultValue ?? "",
  };
}

export function extractFieldValues(field: TableFilterFieldConfig, values: TableFilterValues): TableFilterValues {
  const keys = getFieldValueKeys(field);
  const extracted: TableFilterValues = {};

  for (const key of keys) {
    extracted[key] = values[key] ?? "";
  }

  return extracted;
}

export function mergeFieldValues(
  values: TableFilterValues,
  field: TableFilterFieldConfig,
  fieldValues: TableFilterValues,
): TableFilterValues {
  const nextValues = { ...values };

  for (const key of getFieldValueKeys(field)) {
    nextValues[key] = fieldValues[key] ?? "";
  }

  return nextValues;
}

export function clearFieldValues(values: TableFilterValues, field: TableFilterFieldConfig): TableFilterValues {
  return mergeFieldValues(values, field, getFieldDefaultValues(field));
}

export function isFieldApplied(field: TableFilterFieldConfig, values: TableFilterValues): boolean {
  const defaults = getFieldDefaultValues(field);

  for (const key of Object.keys(defaults)) {
    if ((values[key] ?? defaults[key]) !== defaults[key]) {
      return true;
    }
  }

  return false;
}

export function parseMultiSelectValue(value: string): string[] {
  if (!value.trim()) return [];

  return value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

export function serializeMultiSelectValue(values: string[]): string {
  return values.join(",");
}

export function parseDateValue(value?: string): Date | undefined {
  if (!value) return undefined;
  if (isDateOnlyString(value)) return dateOnlyToDatePickerValue(value) ?? undefined;

  const directDate = new Date(value);
  if (!Number.isNaN(directDate.getTime())) {
    return directDate;
  }

  return undefined;
}

export function toDateInputValue(value?: Date): string {
  if (!value) return "";
  return datePickerValueToDateOnly(value) ?? "";
}

export function toDateTimeInputValue(value?: Date, boundary: "start" | "end" = "start"): string {
  if (!value) return "";

  const normalized = boundary === "start" ? startOfDay(value) : endOfDay(value);
  return format(normalized, "yyyy-MM-dd'T'HH:mm");
}

export function getFieldValueLabel(field: TableFilterFieldConfig, values: TableFilterValues): string {
  if (field.type === "dateRange") {
    const from = values[field.fromKey] ?? "";
    const to = values[field.toKey] ?? "";
    if (!from && !to) {
      return "Select date range";
    }

    const fromDate = parseDateValue(from);
    const toDate = parseDateValue(to);
    const fromLabel = fromDate ? format(fromDate, "M/d/yyyy") : "-";
    const toLabel = toDate ? format(toDate, "M/d/yyyy") : "-";
    return `${fromLabel} - ${toLabel}`;
  }

  if (field.type === "numberRange") {
    const min = values[field.minKey] ?? "";
    const max = values[field.maxKey] ?? "";
    if (!min && !max) {
      return "Enter range";
    }

    return `${min || "-"} - ${max || "-"}`;
  }

  const rawValue = values[field.key] ?? "";

  if (field.type === "select" || field.type === "lookup" || field.type === "asyncSelect") {
    return field.options.find((option) => option.value === rawValue)?.label ?? field.placeholder ?? "Enter value";
  }

  if (field.type === "multiSelect") {
    const selectedValues = parseMultiSelectValue(rawValue);
    if (!selectedValues.length) {
      return field.placeholder ?? "Select values";
    }

    return selectedValues
      .map((value) => field.options.find((option) => option.value === value)?.label ?? value)
      .join(", ");
  }

  if (field.type === "boolean") {
    if (rawValue === "true") return field.trueLabel ?? "True";
    if (rawValue === "false") return field.falseLabel ?? "False";
    return field.placeholder ?? "Enter value";
  }

  if (field.type === "dateTime") {
    const parsed = parseDateValue(rawValue);
    if (!parsed) {
      return field.placeholder ?? "Select date";
    }
    return format(parsed, "M/d/yyyy p");
  }

  return rawValue || field.placeholder || "Enter value";
}

export function countActiveFilters(fields: TableFilterFieldConfig[], values: TableFilterValues): number {
  return fields.reduce((count, field) => count + (isFieldApplied(field, values) ? 1 : 0), 0);
}

export function isFieldVisible(field: TableFilterFieldConfig, values?: TableFilterValues): boolean {
  if (field.hidden === true) return false;
  if (!field.visibleWhen) return true;
  return values?.[field.visibleWhen.key] === field.visibleWhen.equals;
}

export function getSearchDebounceMs(search?: TableFilterSearchConfig): number {
  if (!search) return 0;
  if (typeof search.debounceMs !== "number") return 0;
  if (search.debounceMs < 0) return 0;
  return search.debounceMs;
}
